import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import { openModal } from '@/components/modals/modals.utils';
import { Button } from '@/components/ui/button';
import { UploadImage } from '@/components/ui/upload-image';
import { env } from '@/constants/env';
import useResizeObserver from '@/hooks/use-resize-observer';

import { CollapsableCard } from './collapsable-card';

interface ImageUploadProps {
  title?: string;
  estateId?: string;
  imageId?: string;
  replacing?: boolean;
  onUpload: (image: File) => void;
  onCancelUpload?: () => void;
  onReplaceSelected: () => void;
  onSelectImage: (id: string) => void;
}

export const ImageUpload = ({
  title,
  estateId,
  imageId,
  replacing,
  onUpload,
  onCancelUpload,
  onReplaceSelected,
  onSelectImage,
}: ImageUploadProps) => {
  const { t } = useTranslation();
  const isMobileView = !useResizeObserver(640);

  const handleTakePhoto = () => {
    if (!isMobile) {
      window.alert(t('noCameraError'));
    }
  };

  const handleUpload = (image: File) => {
    onUpload(image);
    onCancelUpload && onCancelUpload();
  };

  const handleReplaceSelected = () => {
    onReplaceSelected();
    onCancelUpload && onCancelUpload();
  };

  const handleSelectFromMediaLibrary = () => {
    if (estateId) {
      openModal('media-library', { estateId, imageId, onSelectImage });
      onCancelUpload && onCancelUpload();
    }
  };

  const handleOpenMobileImageUploadModal = () => {
    openModal('mobile-image-upload', { onUpload: handleUpload });
  };

  const uploadOptionsContent = (
    <div className="flex flex-1 flex-col gap-1 sm:gap-2">
      <Button className="relative flex-1 border border-solid border-primary-light bg-white py-2 text-xs text-primary-light lg:text-base">
        {t('upload')}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity hover:opacity-100">
          <UploadImage onUpload={handleUpload} hideIcon />
        </div>
      </Button>
      {onReplaceSelected ? (
        <Button
          className="flex-1 border border-solid border-primary-light bg-white py-2 text-xs text-primary-light lg:text-base"
          onClick={handleReplaceSelected}
        >
          {t('replaceWithSelected')}
        </Button>
      ) : null}
      <Button
        className="hidden flex-1 border border-solid border-primary-light bg-white py-2 text-xs text-primary-light sm:block lg:text-base"
        onClick={handleTakePhoto}
      >
        {t('createPhoto')}
      </Button>
      <Button
        className="!h-auto min-h-[43px] flex-1 whitespace-break-spaces border border-solid border-primary-light bg-white py-2 text-xs text-primary-light lg:text-base"
        onClick={handleSelectFromMediaLibrary}
      >
        {t('selectFromMediaLibrary')}
      </Button>
      {onCancelUpload ? (
        <Button
          className="flex-1 border border-solid border-primary-light bg-white py-2 text-xs text-primary-light lg:text-base"
          onClick={onCancelUpload}
        >
          {t('cancel')}
        </Button>
      ) : null}
    </div>
  );

  return (
    <>
      <CollapsableCard
        title={title ?? t('newRenderingFromImage')}
        onClickTitle={handleOpenMobileImageUploadModal}
        hideContent={isMobileView && !replacing}
      >
        <div className="flex flex-wrap items-center justify-center gap-2">
          {imageId ? (
            <img
              className="block h-32 rounded-sm object-cover sm:hidden lg:h-48"
              src={`${env.API_URL}/images/${imageId}`}
            />
          ) : null}
          {uploadOptionsContent}
        </div>
      </CollapsableCard>
    </>
  );
};
